import { createFileRoute } from "@tanstack/react-router";

import React from "react";
import { useTranslation } from "react-i18next";
import DEVICE_CONTEXT from "src/context/customer/device";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";
import { Button } from "src/components/base/button";
import { ClipboardDocumentListIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import SETTINGS_CONTEXT from "src/context/customer/settings";
import { Subheading } from "src/components/base/heading";
import { Badge } from "src/components/base/badges/base";
import { Divider } from "src/components/base/divider";
import { ApiCustomer } from "src/api/api_customer";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link GeneralDeviceView}
 */
export type GeneralDeviceViewProps = {};

/**
 * A view for a single device
 */
function GeneralDeviceView(props: GeneralDeviceViewProps) {
    const [t] = useTranslation("customer-device-view");
    const [tg] = useTranslation();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);
    const deviceContext = React.useContext(DEVICE_CONTEXT);

    const data = Route.useLoaderData();

    const device = deviceContext.device;
    const settings = settingsContext.settings;

    return (
        <div className={"max-w-xl"}>
            <Subheading>{t("heading.data")}</Subheading>
            <DescriptionList className={"mt-3"}>
                {device.user && (
                    <>
                        <DescriptionTerm>{t("label.owner")}</DescriptionTerm>
                        <DescriptionDetails>{device.user.display_name}</DescriptionDetails>
                    </>
                )}

                <DescriptionTerm>{t("label.dns-names")}</DescriptionTerm>
                <DescriptionDetails className={"flex flex-col"}>
                    {[device.dnsname, ...settings.headscale_domains.map((d) => `${device.dnsname}.${d}`)].map(
                        (name, idx) => (
                            <span key={idx}>{name}</span>
                        ),
                    )}
                    {data.list.length > 0 && (
                        <>
                            <Divider className={"my-2"} />
                            <div className={"flex flex-col gap-2"}>
                                {data.list.map((dns) => (
                                    <div className={"flex items-center gap-2"}>
                                        <Badge color={"blue"} className={"w-fit"}>
                                            {t("label.extra-dns")}
                                        </Badge>
                                        <span className={"w-50 truncate"}>{dns.from_dns}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </DescriptionDetails>
                {device.ipv4 && (
                    <>
                        <DescriptionTerm>{t("label.ipv4")}</DescriptionTerm>
                        <DescriptionDetails className={"flex items-center gap-1"}>
                            <span>{device.ipv4}</span>
                            <Button
                                plain={true}
                                onClick={async () => {
                                    device.ipv4 && (await navigator.clipboard.writeText(device.ipv4));
                                    toast.success(tg("toast.copied-to-clipboard"));
                                }}
                            >
                                <ClipboardDocumentListIcon />
                            </Button>
                        </DescriptionDetails>
                    </>
                )}

                {device.ipv6 && (
                    <>
                        <DescriptionTerm>{t("label.ipv6")}</DescriptionTerm>
                        <DescriptionDetails className={"flex items-center gap-1"}>
                            <span>{device.ipv6}</span>
                            <Button
                                plain={true}
                                onClick={async () => {
                                    device.ipv6 && (await navigator.clipboard.writeText(device.ipv6));
                                    toast.success(tg("toast.copied-to-clipboard"));
                                }}
                            >
                                <ClipboardDocumentListIcon />
                            </Button>
                        </DescriptionDetails>
                    </>
                )}

                <DescriptionTerm>{t("label.hostname")}</DescriptionTerm>
                <DescriptionDetails>{device.hostname}</DescriptionDetails>

                <DescriptionTerm>{t("label.first-seen")}</DescriptionTerm>
                <DescriptionDetails>{new Date(device.created_at).toLocaleDateString("de-de")}</DescriptionDetails>

                <DescriptionTerm>{t("label.last-seen")}</DescriptionTerm>
                <DescriptionDetails>
                    {device.online ? (
                        <div className={"flex items-center gap-2"}>
                            <div className={"size-2 animate-pulse rounded bg-green-500"} />
                            <span>{t("label.online")}</span>
                        </div>
                    ) : device.last_seen ? (
                        new Date(device.last_seen).toLocaleString("de-de")
                    ) : (
                        "?"
                    )}
                </DescriptionDetails>

                <DescriptionTerm>{t("label.expiry")}</DescriptionTerm>
                <DescriptionDetails>
                    {device.expiry ? new Date(device.expiry).toLocaleDateString("de-de") : "-"}
                </DescriptionDetails>

                {device.tags && (
                    <>
                        <DescriptionTerm>{t("label.tags")}</DescriptionTerm>
                        <DescriptionDetails className={"flex flex-wrap gap-1"}>
                            {device.tags.map((tag) => (
                                <TagBadge key={tag.uuid} tag={tag} />
                            ))}
                        </DescriptionDetails>
                    </>
                )}
            </DescriptionList>
        </div>
    );
}

export const Route = createFileRoute("/_customer/c/devices/$deviceId/_device/general")({
    component: GeneralDeviceView,
    loader: async ({ params }) => await ApiCustomer.dns.getExtraDnsByDevice(params.deviceId),
});
